/**
 * The file's unimported columns, gathered by what can be done about them.
 *
 * Every column used to carry its own sentence, and it was the same sentence:
 * a file with eight unrecognised columns said "this table has no such field.
 * To import it, choose A column of your file on a field above and pick it"
 * eight times, once per line. A fact repeated per row is noise - it gets read
 * once and skipped after that, including by the person it was written for,
 * who then cannot see which of the eight lines differs from the others.
 *
 * So the CLASS carries the words and the column carries only what is its own:
 * the name the file gave it, and the field of ours it turned out to be. The
 * plan screen gathers rows by reason for exactly this reason; this is the
 * same idea applied to the other list.
 */
import { UnusedColumn } from './mapping-rows';

/**
 * What can be done about a column, which is the only thing the wording turns
 * on. Two axes: whether we recognise the name at all, and - when we do -
 * whether the field it belongs to will accept an import anyway. "Calculated"
 * and "read-only" are then two different explanations of why it will not be
 * written by default, and they send a reader to different places: one to the
 * thing that recalculates it, the other to nothing at all.
 */
export type UnusedKind =
  | 'unknown'
  | 'calculated-overridable'
  | 'readonly-overridable'
  | 'calculated-fixed'
  | 'readonly-fixed';

export interface UnusedGroup {
  kind: UnusedKind;
  /** The heading. Short enough to sit next to a count. */
  title: string;
  /** Said ONCE for the group: what these are, and what to do about them. */
  sentence: string;
  columns: UnusedColumn[];
}

const COPY: Readonly<Record<UnusedKind, { title: string; sentence: string }>> = {
  unknown: {
    title: 'Not a field in this table',
    sentence: 'This table has nothing by these names. To bring one in, choose'
      + ' “A column” on the field above that should hold it, and pick'
      + ' it there.',
  },
  'calculated-overridable': {
    title: 'Calculated by default',
    sentence: 'This table works these out itself. To import your file’s'
      + ' values instead, pick the column on that field’s row above.',
  },
  'readonly-overridable': {
    title: 'Read-only by default',
    sentence: 'An import leaves these alone unless it is asked not to. To'
      + ' import your file’s values, pick the column on that field’s'
      + ' row above.',
  },
  'calculated-fixed': {
    title: 'Calculated by this table',
    sentence: 'This table works these out itself, and an import cannot set'
      + ' them. Nothing for you to do.',
  },
  'readonly-fixed': {
    title: 'Never imported',
    sentence: 'An import never writes these. Nothing for you to do.',
  },
};

/**
 * Reading order: the ones that need a decision first, the ones there is
 * nothing to do about last. A person scanning this list is looking for work,
 * and the groups that are not work should not be in the way of finding it.
 */
const ORDER: readonly UnusedKind[] = [
  'unknown',
  'calculated-overridable',
  'readonly-overridable',
  'calculated-fixed',
  'readonly-fixed',
];

export function kindOf(column: UnusedColumn): UnusedKind {
  if (column.unknown) return 'unknown';
  const why = column.computed ? 'calculated' : 'readonly';
  return `${why}-${column.overridable ? 'overridable' : 'fixed'}` as UnusedKind;
}

/**
 * The groups actually present, in reading order. A kind with no columns is
 * not rendered empty - it is not there at all, so the section is as short as
 * the file allows.
 */
export function groupUnused(columns: readonly UnusedColumn[]): UnusedGroup[] {
  const byKind = new Map<UnusedKind, UnusedColumn[]>();
  for (const column of columns) {
    const kind = kindOf(column);
    const held = byKind.get(kind);
    if (held) held.push(column);
    else byKind.set(kind, [column]);
  }

  return ORDER
    .filter(kind => byKind.has(kind))
    .map(kind => ({ kind, ...COPY[kind], columns: byKind.get(kind) as UnusedColumn[] }));
}
